import React, { forwardRef } from 'react';
import Table from '../../atoms/Table';

import { useSelector } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import {
  getPcmCdNmByPcmCdDivisionFromMasterInfo,
  getPcmCdNmFromMasterInfo,
} from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';
import { ACTION_TAG, COMMODITY_CATEGORY_TAG } from '../../../Common';
import { JapaneseDateStringType, getJapaneseDateTimeAsString } from '../../../utils/MomentUtil';

import PreviewContents from '../../atoms/PreviewContents';
import PreviewMiddleCategoryHeader from '../../atoms/PreviewMiddleCategoryHeader';
import PreviewSmallCategoryHeader from '../../atoms/PreviewSmallCategoryHeader';
import GinieTelephonyTable from '../../molecules/GinieTelephonyTable';
import { ActionInfoDetailState, ProductVariableInfoPreviewState } from '../../../redux/reducers/CustomerInfoAllReducer';
import { StringOrNull } from '../../../types';
import { DISPLAY_ITEM_ID } from '../../../redux/reducers/MasterInfoReducer';
import { setStringToBrackets } from '../../../utils/StringUtil';

export interface ActionInfoDetailProps {
  actionItem: ActionInfoDetailState;
  index: number;
  negotiationId: number;
  isEditable: boolean;
  openPermissionModal?: (type: string, negotiationId?: string, productId?: string, actionId?: string) => void;
}
const ActionInfoDetailPreview = forwardRef<HTMLDivElement, ActionInfoDetailProps>(
  ({ actionItem, index, negotiationId, isEditable, openPermissionModal }, lastDivItemRef) => {
    // custom Hooks
    const masterInfo = useSelector((state: AppState) => state.masterInfo);
    const customerInfo = useSelector((state: AppState) => state.customerInfoAll);

    // actionType別 PreviewContents uniqueId 命名処理
    const uniqueActionTypeWord =
      actionItem.actionType === '01' ? 'action-come' : actionItem.actionType === '02' ? 'action-call' : 'action-callback';

    // actionType別 スケジュール日時 命名処理
    const labelScheduledDate =
      actionItem.actionType === '01' ? '来店予定日時' : actionItem.actionType === '02' ? '架電予定日時' : '受電予定日時';

    const actionScheduledDisplayDate =
      actionItem.dayType === '1'
        ? '平日'
        : actionItem.dayType === '2'
        ? '休日'
        : actionItem.dayType === '3'
        ? getJapaneseDateTimeAsString(actionItem.actionDate, JapaneseDateStringType.YEAR_MONTH_DATE)
        : '';

    const getActionScheduledDisplayTime = (type: StringOrNull, value: StringOrNull): string => {
      if (!value) {
        return '指定なし';
      }
      if (type === '01') {
        return value;
      }
      return `${value.substring(0, 2)}時台`;
    };
    const actionScheduledDisplayTime =
      actionItem.actionTimeType === '01'
        ? !actionItem.actionTimeFrom && !actionItem.actionTimeTo
          ? ''
          : `${getActionScheduledDisplayTime(actionItem.actionType, actionItem.actionTimeFrom)}～${getActionScheduledDisplayTime(
              actionItem.actionType,
              actionItem.actionTimeTo
            )}`
        : actionItem.actionTimeType === '02'
        ? 'AM'
        : actionItem.actionTimeType === '03'
        ? 'PM'
        : '';
    // スケジュール：XX予定日時(XX -> 来店 or 架電 or 受電)
    const scheduledActionDateTime = `${actionScheduledDisplayDate} ${actionScheduledDisplayTime}`.trim();

    const getDisplayBrandName = (variableInfo: ProductVariableInfoPreviewState[]): string => {
      if (variableInfo.length === 0) {
        return '';
      }
      const displayItemId = DISPLAY_ITEM_ID.COMMODITY_CATEGORY_BRAND;
      const brandTypeCode = variableInfo.find((item) => item.displayItemId === displayItemId)?.setValue || '';
      const brandTypeName = getPcmCdNmFromMasterInfo(masterInfo, displayItemId, brandTypeCode);
      if (!brandTypeName) {
        return '';
      }
      return `(${brandTypeName}) `;
    };

    // 対象商品：対応商品
    const relationshipProduct = (productId: number): string => {
      let commodityCategoryTag = '';
      customerInfo.negotiationInfoDetail.length &&
        customerInfo.negotiationInfoDetail.forEach((negotiationItem) => {
          negotiationItem.negotiationId === negotiationId &&
            negotiationItem.productInfoDetail.length &&
            [...negotiationItem.productInfoDetail]
              .sort((a, b) => a.productId - b.productId)
              .forEach((productItem, index) => {
                if (productItem.productId === productId) {
                  commodityCategoryTag = `商品${index + 1} - ${getDisplayBrandName(productItem.productVariableInfo)}${
                    COMMODITY_CATEGORY_TAG[productItem.commodityCategoryType].name
                  }`;
                }
              });
        });
      return commodityCategoryTag;
    };

    const displayRelationshipProduct = (relationshipProductId: number[]): string => {
      const relationshipProductValue = relationshipProductId.length
        ? [...relationshipProductId]
            .sort((a, b) => a - b)
            .map((productId) => {
              return relationshipProduct(productId);
            })
        : [''];
      return relationshipProductValue.join('\n');
    };

    // アクション結果：対応日付
    const actionCompleteDate = getJapaneseDateTimeAsString(actionItem.actionCompleteDate, JapaneseDateStringType.YEAR_MONTH_DATE);

    // アクション結果：対応時間
    const actionCompleteTime = actionItem.actionCompleteTime ? `${actionItem.actionCompleteTime}頃` : '';
    // アクション結果：対応日時
    const displayActionCompleteDate = `${actionCompleteDate} ${actionCompleteTime}`.trim();

    // GINIEテレフォニー連携：GINIEテレフォニー連携
    const displayContactMsn = () => {
      const contactMsn: string[] = [];
      customerInfo.contactMsn1 && contactMsn.push(`日中：${customerInfo.contactMsn1}`);
      customerInfo.contactMsn2 && contactMsn.push(`緊急：${customerInfo.contactMsn2}`);
      return contactMsn.join('\n');
    };

    // アクション結果：対応結果
    const displayActionStatusType =
      actionItem.actionStatusType === '0100'
        ? ''
        : actionItem.actionStatusType === '0200'
        ? ''
        : actionItem.actionStatusType === '0300'
        ? ''
        : getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0108', actionItem.actionStatusType ? actionItem.actionStatusType : '');
    // 対応者
    // 担当者名 + (担当者コード)
    // 上記の形式で表示する。但し、名称、コードどちらにも値が入っていない場合は、出力しない
    const hyphens = '----------';
    const actionSalesmanName =
      !actionItem.actionSalesmanName && !actionItem.staffCrewCd
        ? ''
        : `${actionItem.actionSalesmanName || hyphens}${setStringToBrackets(actionItem.staffCrewCd || hyphens)}`;

    return (
      <div ref={lastDivItemRef} id={'negotiation' + negotiationId + 'action' + actionItem.actionId}>
        <Table key={actionItem.actionId} className='preview-table u-mt32'>
          {/* header (middle) */}
          <PreviewMiddleCategoryHeader
            title={`アクション${index + 1} - ${ACTION_TAG[actionItem.actionType].name}`}
            uniqueId={`${uniqueActionTypeWord}-header-${actionItem.actionId}`}
            editInfo={
              isEditable
                ? {
                    uniqueId: `${actionItem.actionId}`,
                    label: '編集',
                    onClickEditHandler: () =>
                      openPermissionModal && openPermissionModal('onEditAction', `${negotiationId}`, '', `${actionItem.actionId}`),
                  }
                : undefined
            }
          />
          {/* アクション：架電のみ表示 */}
          {actionItem.actionType === '02' && (
            <>
              <Table.TBody>
                {/* header (small) */}
                <PreviewSmallCategoryHeader
                  title='GINIEテレフォニー連携'
                  uniqueId={`${uniqueActionTypeWord}-ginie-telephony-heading-${actionItem.actionId}`}
                />

                {/* preview contents */}
                <PreviewContents
                  title='GINIEテレフォニー連携'
                  uniqueId={`${uniqueActionTypeWord}-contact-msn-${actionItem.actionId}`}
                  paragraphValue={displayContactMsn()}
                />
              </Table.TBody>
            </>
          )}
          <Table.TBody>
            {/* header (small) */}
            <PreviewSmallCategoryHeader title='スケジュール' uniqueId={`${uniqueActionTypeWord}-schedule-heading-${actionItem.actionId}`} />

            {/* preview contents */}
            <PreviewContents
              title={`${labelScheduledDate}`}
              uniqueId={`${uniqueActionTypeWord}-scheduled-date-${actionItem.actionId}`}
              paragraphValue={scheduledActionDateTime}
            />
          </Table.TBody>
          <Table.TBody>
            {/* header (small) */}
            <PreviewSmallCategoryHeader
              title='対象商品'
              uniqueId={`${uniqueActionTypeWord}-relationship-product-heading-${actionItem.actionId}`}
            />

            {/* preview contents */}
            <PreviewContents
              title='対象商品'
              uniqueId={`${uniqueActionTypeWord}-relationship-product-${actionItem.actionId}`}
              paragraphValue={actionItem.relationshipProductId ? displayRelationshipProduct(actionItem.relationshipProductId) : ''}
            />
          </Table.TBody>

          {actionItem.actionType === '02' && (
            <>
              <Table.TBody>
                {/* header (small) */}
                <PreviewSmallCategoryHeader
                  title='GINIEテレフォニー発信履歴'
                  uniqueId={`${uniqueActionTypeWord}-day-type-heading-${actionItem.actionId}`}
                />

                {/* preview contents */}
                <PreviewContents title='GINIEテレフォニー発信履歴' uniqueId={`${uniqueActionTypeWord}-day-type-${actionItem.actionId}`}>
                  <div className='scrollable-area'>
                    <GinieTelephonyTable data={actionItem.actionGinieTelephoneRelationList} />
                  </div>
                </PreviewContents>
              </Table.TBody>
            </>
          )}

          <Table.TBody>
            {/* header (small) */}
            <PreviewSmallCategoryHeader title='アクション結果' uniqueId={`${uniqueActionTypeWord}-result-heading-${actionItem.actionId}`} />

            {/* preview contents */}
            <PreviewContents
              title='対応日時'
              uniqueId={`${uniqueActionTypeWord}-complete-date-${actionItem.actionId}`}
              paragraphValue={displayActionCompleteDate}
            />

            {/* preview contents */}
            <PreviewContents
              title='対応者'
              uniqueId={`${uniqueActionTypeWord}-salesman-name-${actionItem.actionId}`}
              paragraphValue={actionSalesmanName}
            />
            {/* preview contents */}
            <PreviewContents
              title='対応結果'
              uniqueId={`${uniqueActionTypeWord}-status-type-${actionItem.actionId}`}
              paragraphValue={displayActionStatusType}
            />
          </Table.TBody>
          <Table.TBody>
            {/* header (small) */}
            <PreviewSmallCategoryHeader title='メモ' uniqueId={`${uniqueActionTypeWord}-memo-heading-${actionItem.actionId}`} />

            {/* preview contents */}
            <PreviewContents
              title='メモ'
              uniqueId={`${uniqueActionTypeWord}-memo-${actionItem.actionId}`}
              paragraphValue={actionItem.actionMemo ? actionItem.actionMemo : ''}
            />
          </Table.TBody>
        </Table>
      </div>
    );
  }
);

export default ActionInfoDetailPreview;
